import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '@domains/shared/domain/application-service/language.service';
import { ThemeService } from '@domains/shared/domain/application-service/theme.service';
import { LanguageStateService } from '@domains/shared/domain/application-service/language-state.service';
import { defaultUiLanguage, supportedUiLanguages } from '@domains/shared/domain/models/language.model';
import { DeviceReportService } from '@domains/identity/domain/application-services/device-report.service';
import { IdentityFacade } from '@domains/identity/domain/application-services/identity.facade';
import { MediaAccessRefreshService } from '@domains/identity/domain/application-services/media-access-refresh.service';
import { Subscription } from 'rxjs';

/** Parametr, kterým odkazy z e-mailů říkají, v jakém jazyce má rozhraní naběhnout. */
export const languageQueryParam = 'lang';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'elearning';

  subs: Subscription = new Subscription();

  constructor(
    private translate: TranslateService,
    private languageService: LanguageService,
    private languageState: LanguageStateService,
    private themeService: ThemeService,
    private identityFacade: IdentityFacade,
    private deviceReport: DeviceReportService,
    private mediaAccessRefresh: MediaAccessRefreshService,
  ) {
    this.setTranslate();

    // Motiv se musí vykreslit dřív, než se objeví první obrazovka — jinak by
    // uživateli s tmavým režimem na okamžik probliklo bílé pozadí.
    this.themeService.init();
  }

  ngOnInit(): void {
    // Cookie k firemním obrázkům a videím se po návratu do karty obnoví dřív, než
    // stránka o obrázek požádá (EL-2301). Nepřihlášenému se nic neposílá.
    this.mediaAccessRefresh.start();

    // Na čem uživatel aplikaci používá, se hlásí jednou po přihlášení. Dřív to nejde:
    // požadavek potřebuje token, jinak by ho server nepřiřadil k nikomu.
    this.subs.add(this.identityFacade.getLoggedUser$.subscribe({
      next: (user) => {
        if (user) {
          this.deviceReport.report();
        }
      },
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private setTranslate() {
    this.translate.addLangs(supportedUiLanguages);
    this.translate.setDefaultLang(defaultUiLanguage);

    // Jazyk přihlášeného uživatele nastaví IdentityFacade, jakmile ho načte.
    // Než k tomu dojde (login, aktivace účtu, reset hesla), rozhoduje tohle pořadí.
    this.languageService.use(this.resolveStartupLanguage());
  }

  /**
   * Vlastní volba > odkaz z e-mailu > naposledy použitý jazyk > jazyk prohlížeče > výchozí.
   *
   * Volba vlaječkou je nahoře schválně: `?lang=` nese jazyk profilu, a ten řídí e-maily,
   * ne rozhraní. Jinak by jediný proklik z e-mailu přepsal jazyk, který si uživatel na
   * tomhle počítači vědomě zvolil. Pro toho, kdo si nevybral, zůstává odkaz nejsilnějším
   * signálem — víme, komu jsme ho poslali.
   */
  private resolveStartupLanguage(): string | undefined {
    // Čte se z window, ne z routeru — ten v konstruktoru ještě nic neví
    // a odkaz může mířit na libovolnou routu.
    const fromLink = new URLSearchParams(window.location.search).get(languageQueryParam);

    return this.languageState.chosen
      ?? fromLink
      ?? this.languageState.remembered
      ?? this.translate.getBrowserLang();
  }
}
